/* useContent.js — public-site hook for reading per-page CMS overrides.
 *
 * The backend stores a `data` JSON blob per page under /api/content/{key}.
 * The hook merges the API response on top of the page-supplied `defaults`
 * so the hand-coded design stays intact when a key is missing/empty.
 *
 *   const c = useContent("home", HOME_DEFAULTS);
 *   <h1>{c.hero.title}</h1>
 *
 * Lookups always succeed — `get(c, "hero.title", fallback)` is also exposed
 * for sites that prefer dotted-path access. The merge is a *deep* merge so
 * nested objects are unioned rather than replaced.
 */
import { useEffect, useMemo, useRef, useState } from "react";

const BASE = process.env.REACT_APP_BACKEND_URL;

// In-memory cache: same key fetches once per page load.
const _cache = new Map(); // key -> { promise, data }

export const fetchContent = (key) => {
  if (_cache.has(key)) return _cache.get(key).promise;
  const promise = fetch(`${BASE}/api/content/${key}`, {
    credentials: "omit",
  })
    .then((r) => (r.ok ? r.json() : { key, data: {} }))
    .then((doc) => {
      const data = (doc && doc.data) || {};
      _cache.set(key, { promise, data });
      return data;
    })
    .catch(() => {
      const data = {};
      _cache.set(key, { promise, data });
      return data;
    });
  _cache.set(key, { promise, data: undefined });
  return promise;
};

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

const deepMerge = (a, b) => {
  if (!isPlainObject(a) || !isPlainObject(b)) return b ?? a;
  const out = { ...a };
  for (const k of Object.keys(b)) {
    if (b[k] === undefined || b[k] === null || b[k] === "") continue;
    if (isPlainObject(a[k]) && isPlainObject(b[k])) {
      out[k] = deepMerge(a[k], b[k]);
    } else {
      out[k] = b[k];
    }
  }
  return out;
};

export function useContent(key, defaults = {}) {
  const cached = _cache.get(key)?.data;
  const [override, setOverride] = useState(cached);
  const [ready, setReady] = useState(cached !== undefined);
  const defaultsRef = useRef(defaults);
  // We deliberately avoid making `defaults` a dep — it is the static fallback.

  useEffect(() => {
    let cancelled = false;
    fetchContent(key).then((data) => {
      if (!cancelled) {
        setOverride(data || {});
        setReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return useMemo(() => {
    const merged = deepMerge(defaultsRef.current, override || {});
    // Attach the ready flag as a non-enumerable property so spreads
    // (`{...cms}`) don't carry it into rendered JSX.
    Object.defineProperty(merged, "__ready", {
      value: ready,
      enumerable: false,
      writable: false,
    });
    return merged;
  }, [override, ready]);
}

/** Has CMS data for this key arrived from the network yet? */
export function useContentReady(key) {
  const [ready, setReady] = useState(_cache.get(key)?.data !== undefined);
  useEffect(() => {
    let cancelled = false;
    fetchContent(key).then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [key]);
  return ready;
}

/** Dotted path getter with default — c.hero.title === get(c, "hero.title"). */
export function get(obj, path, fallback) {
  if (!obj) return fallback;
  const parts = String(path).split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return fallback;
    cur = cur[p];
  }
  return cur === undefined || cur === null || cur === "" ? fallback : cur;
}

/** Resolve a media URL: pass-through external URLs, prefix relative ones. */
export function mediaUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
  return `${BASE}${url.startsWith("/") ? "" : "/"}${url}`;
}

/**
 * Invalidate the in-memory cache for one content key (or all keys when
 * called with no argument). The admin save path calls this so that any
 * Preview tab still on the same browser session re-fetches on next mount.
 */
export function invalidateContent(key) {
  if (key) _cache.delete(key);
  else _cache.clear();
}
